import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import { Alert } from "@/components/ui/alert";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatAgoFromISO, formatDate } from "@/lib/formatters";
import { PageProps } from "@/types/PageProps";
import { Adjustment } from "@/types/serializers";
import { Head, Link } from "@inertiajs/react";

import { AlertCircle, ArrowRightIcon, DownloadIcon } from "lucide-react";
type Props = PageProps & {
  recent_adjustments: Adjustment[];
  prompt_nps?: boolean;
};

export default function Dashboard({ user, recent_adjustments, errors, notice, prompt_nps }: Props) {
  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title="Dashboard" />
      <div className="py-8 space-y-8">
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle>Start a new adjustment</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Start calculating time-based adjustments based on the data from your own MLS.
            </p>

            <Link href="/adjustments/new" className={buttonVariants()}>
              New Adjustment <ArrowRightIcon className="w-4 h-4" />
            </Link>
            {prompt_nps && <span className="prompt_nps" />}
          </CardContent>
        </Card>

        <Card className="max-w-4xl">
          <CardHeader>
            <CardTitle>Recent adjustments</CardTitle>
          </CardHeader>

          <CardContent>
            {recent_adjustments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent adjustments.</p>
            ) : (
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Address</TableHead>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent_adjustments.map((adjustment) => (
                      <TableRow key={adjustment.public_id}>
                        <TableCell>
                          <Link href={`/adjustments/${adjustment.public_id}`} className="underline">
                            {adjustment.address}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {adjustment.effective_date &&
                            formatDate(new Date(adjustment.effective_date).getTime())}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatAgoFromISO(adjustment.created_at)}
                        </TableCell>
                        <TableCell>
                          {adjustment.current_adjustment_pdf_url && (
                            <a
                              href={adjustment.current_adjustment_pdf_url}
                              target="_blank"
                              className={buttonVariants({ variant: "outline", size: "xs" })}
                            >
                              Download PDF <DownloadIcon className="w-4 h-4" />
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="pt-4">
                  <Link href="/adjustments" className={buttonVariants({ variant: "outline" })}>
                    View all
                    <ArrowRightIcon className="h-2 w-2" />
                  </Link>
                </div>
                {
                  <Alert className="mt-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        After 30 days, your reports will become read-only. You'll still be able to
                        access the PDF summary.
                      </span>
                    </div>
                  </Alert>
                }
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AuthenticatedLayout>
  );
}
